// Guests directory (spec §6.5), backed by the Task-23 fix-round addition
// `admin-user-data`'s `search` action (a SECURITY DEFINER RPC over
// auth.users.email / profiles.display_name — see migration
// 20260926000040). Replaces piggybacking on admin-bookings' search, which
// only ever surfaced a guest who already has a booking, and which the
// previous version of this file read as `row.user_id` — a column
// admin_booking_search returns as `guest_id`, so that mapping was always
// empty. A direct account-id paste still works for jumping straight to
// /guests/$id (e.g. from a booking detail's guest.id).
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';

export const Route = createFileRoute('/guests/')({
  component: GuestsList,
});

interface SearchResultRow {
  id: string;
  email: string | null;
  display_name: string | null;
  created_at: string;
}

// Fix round 2: minimum 3 characters, matching the server's own floor
// (admin_ops_lib.ts#parseUserDataRequest) — a 1-2 char query is an
// expensive near-full-scan ILIKE for almost no signal.
const MIN_QUERY_LENGTH = 3;

function useGuestSearch(query: string) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-user-data', 'search', query],
    queryFn: () => callEdgeFunction<{ results: SearchResultRow[] }>(supabase, 'admin-user-data', { action: 'search', query }),
    enabled: query.length >= MIN_QUERY_LENGTH,
  });
}

function GuestsList() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [committed, setCommitted] = useState('');
  const [jumpId, setJumpId] = useState('');

  const { data, isLoading, error } = useGuestSearch(committed);
  const results = data?.results ?? [];

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Guests</h1>
        <p className="mt-sm text-body text-ink-2">Search by email or display name, or jump straight to an account id.</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (jumpId.trim()) void navigate({ to: '/guests/$id', params: { id: jumpId.trim() } });
        }}
        className="flex flex-wrap items-center gap-sm"
      >
        <input
          type="text"
          value={jumpId}
          onChange={(e) => setJumpId(e.target.value)}
          placeholder="Guest account id…"
          className="rounded-button border border-border bg-surface px-md py-sm text-body text-ink"
        />
        <button type="submit" className="rounded-button border border-border px-md py-sm text-body text-ink">
          Open
        </button>
      </form>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = input.trim();
          if (trimmed.length >= MIN_QUERY_LENGTH) setCommitted(trimmed);
        }}
        className="flex flex-wrap items-center gap-sm"
      >
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search by email or name…"
          className="min-w-[280px] flex-1 rounded-button border border-border bg-surface px-md py-sm text-body text-ink"
        />
        <button
          type="submit"
          disabled={input.trim().length > 0 && input.trim().length < MIN_QUERY_LENGTH}
          className="rounded-button bg-canyon px-md py-sm text-body font-semibold text-white disabled:opacity-50"
        >
          Search
        </button>
      </form>
      {input.trim().length > 0 && input.trim().length < MIN_QUERY_LENGTH ? (
        <p className="text-caption text-ink-2">Type at least {MIN_QUERY_LENGTH} characters to search.</p>
      ) : null}

      {isLoading ? <p className="text-body text-ink-2">Searching…</p> : null}
      {error ? <p className="text-body text-danger">{(error as Error).message}</p> : null}

      {!isLoading && !error && committed.length >= MIN_QUERY_LENGTH && results.length === 0 ? (
        <p className="rounded-card border border-border bg-surface p-xl text-center text-body text-ink-2">No guests match.</p>
      ) : null}

      {results.length > 0 ? (
        <div className="flex flex-col gap-sm">
          {results.map((r) => (
            <Link
              key={r.id}
              to="/guests/$id"
              params={{ id: r.id }}
              className="rounded-card border border-border bg-surface p-lg shadow-card hover:bg-surface-2"
            >
              <p className="text-body font-semibold text-ink">{r.display_name ?? r.email ?? r.id}</p>
              <p className="text-caption text-ink-2">{r.email ?? r.id}</p>
            </Link>
          ))}
        </div>
      ) : null}
    </section>
  );
}
